/* eslint-disable prefer-arrow/prefer-arrow-functions */
import { Button, CloseButton } from '@ui/shared/Button';
import { TEST_TYPES } from '@hawkindynamics/core-client';
import hashIt from 'hash-it';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import * as React from 'react';
import styled from 'styled-components';
import { copProvider } from '../../../data/providers/copProvider';
import {
  activeMetrics,
  IMetrics,
} from '../../../data/providers/metricsProvider';
import {
  ICOP,
  IMetric,
  IMetricMap,
  ITest,
  ITestRepetition,
  Level,
} from '../../../data/types';
import { excludeRepetitions } from '../../../data/writers/repetitions';
import {
  Repetition,
  useEditRepetitions,
} from '../../../hooks/repetitions/useEditRepetitions';
import { useRepetitions } from '../../../hooks/repetitions/useRepetitions';
import { useTest } from '../../../hooks/Tests/useTest';
import { useGraphData } from '../../../hooks/useGraphData';
import { formatDateWithTime } from '../../../utils/date';
import { movementData } from '../../../utils/formatters';
import { testMetaString } from '../../../utils/testMetaString';
import { toActiveMetricList } from '../../../utils/testMetrics';
import { FullHeightColumn } from '../../layout/Column';
import { Row } from '../../layout/Row';
import { ScrollableArea } from '../../layout/ScrollableArea';
import { useFilename } from '../../reports/utils/useFilename';
import { CardBody } from '../../shared/card/CardBody';
import { CardFooter } from '../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { COPGraph } from '../../shared/graphs/cop/COPGraph';
import { ForceProfile } from '../../shared/graphs/ForceProfile';
import { ForceTime } from '../../shared/graphs/ForceTime';
import { Notable } from '../../shared/pdf/Notable';
import { EPdfMode, isPdf, Pdf } from '../../shared/pdf/Pdf';
import { PdfContainer } from '../../shared/pdf/PdfContainer';
import { PdfStateToggle } from '../../shared/pdf/PdfStateToggle';
import {
  InnerPopupCard,
  PopupCard,
  PopupCardContent,
} from '../../shared/popups/PopupCard';
import { SectionHeader } from '../SectionHeader';
import { Scoring } from './scoring/Scoring';

interface IProps {
  test: ITest;
  testList: ITest[];
  onClose: () => void;
  level: Level;
}

export const Test = (props: IProps): React.ReactElement => {
  const { test: initialTest, onClose, level } = props;

  const [pdfState, setPdfState] = React.useState<EPdfMode>(EPdfMode.DEFAULT);

  const [activeMetricsState, setActiveMetricsState] = React.useState<{
    activeMetricMap: IMetricMap | undefined;
  }>({
    activeMetricMap: undefined,
  });

  const [copData, setCopData] = React.useState<ICOP>();

  const { test, loading: testLoading } = useTest(initialTest);
  const {
    graphData,
    graphRaw,
    timingEvents,
    dataMin,
    loading: graphLoading,
  } = useGraphData(test);

  const showForceProfiles = React.useMemo((): boolean => {
    return (
      test?.testType?.velocity === true &&
      test?.testType?.id !== '2uS5XD5kXmWgIZ5HhQ3A' &&
      test?.testType?.id !== '2jNA63KIvQcqKlxo63we'
    );
  }, [test?.testType]);

  /**
   * @description render the force profiles
   * @returns
   */
  const renderForceProfiles =
    React.useCallback((): React.ReactElement | null => {
      if (!showForceProfiles) return null;

      const [movementStart, movementForce] = movementData(
        test,
        graphRaw?.zForce ?? []
      );
      const movementDisplacement = movementData(
        test,
        graphRaw?.displacement ?? []
      )[1];
      const movementVelocity = movementData(test, graphRaw?.velocity ?? [])[1];

      return (
        <div
          style={{
            flex: 2,
            display: 'flex',
            flexDirection: 'row',
            paddingBottom: 'var(--grid-gap)',
          }}
        >
          <ForceProfile
            loading={graphLoading}
            pdfMode={pdfState}
            force={movementForce}
            roundingPrecision={2}
            secondaryData={movementVelocity}
            secondaryLabel='Velocity'
            secondaryUnits='m/s'
            timingEvents={timingEvents ?? []}
            startTime={movementStart}
            test={test}
            hertz={graphRaw?.hertz}
            secondaryTickSize={0.5}
          />
          <ForceProfile
            style={{
              marginLeft: 'var(--grid-gap)',
              marginBottom: 'var(--grid-gap)',
            }}
            loading={graphLoading}
            pdfMode={pdfState}
            force={movementForce}
            roundingPrecision={2}
            secondaryData={movementDisplacement}
            secondaryLabel='Displacement'
            secondaryUnits='m'
            timingEvents={timingEvents ?? []}
            startTime={movementStart}
            test={test}
            hertz={graphRaw?.hertz}
            secondaryTickSize={0.1}
          />
        </div>
      );
    }, [
      hashIt(test),
      hashIt(graphRaw),
      graphLoading,
      pdfState,
      hashIt(timingEvents),
    ]);

  const handlePdfModeChange = React.useCallback((value: EPdfMode): void => {
    setPdfState(value);
  }, []);

  React.useEffect((): (() => void) => {
    if (!test?.id || test?.testType?.id !== TEST_TYPES.FREE.id) {
      return (): void => {};
    }

    let mounted = true;
    const fetchCopData = async (): Promise<void> => {
      const data = await copProvider(test.id);
      if (mounted) {
        setCopData(data);
      }
    };

    fetchCopData();
    return (): void => {
      mounted = false;
    };
  }, [hashIt(test)]);

  /**
   * @description subscribe to active metrics settings
   */
  React.useEffect((): (() => void) => {
    const subscriber = activeMetrics.subscribe(
      (mActiveMetrics: IMetrics): void => {
        setActiveMetricsState(
          (prev: {
            activeMetricMap: IMetricMap | undefined;
          }): { activeMetricMap: IMetricMap | undefined } => ({
            ...prev,
            activeMetricMap: mActiveMetrics[test?.testType?.id],
          })
        );
      }
    );
    return (): void => subscriber.unsubscribe();
  }, []);

  /**
   * repetitions hook
   */
  const {
    repetitions,
    selectedRepetition,
    selectedRepetitionChangeStream,
    loading: repetitionsLoading,
  } = useRepetitions(test.id);

  /**
   * @description edit repetitions hook
   */
  const editRepetitions = useEditRepetitions({
    enabledRepetitions: repetitions.map(
      (repetition: ITestRepetition): Repetition => ({
        start: repetition.start,
        end: repetition.end,
      })
    ),
    disabledRepetitions: graphRaw.excludedWindows?.map(
      (window: { start: number; end: number }): Repetition => ({
        start: window.start,
        end: window.end,
      })
    ),
    onSave: async (disabled: Repetition[]): Promise<void> => {
      await excludeRepetitions({
        testId: test.id,
        repetitions: disabled,
      });
    },
  });

  /**
   * @description create and memoize the list of active metrics
   */
  const metrics = React.useMemo((): IMetric[] => {
    if (selectedRepetition !== null) {
      return sortBy(Object.values(selectedRepetition.metrics), 'order').filter(
        (value: IMetric): boolean =>
          Boolean(activeMetricsState.activeMetricMap[value?.id])
      );
    } else {
      return toActiveMetricList(test, activeMetricsState.activeMetricMap);
    }
  }, [
    hashIt(selectedRepetition),
    hashIt(test),
    hashIt(Object.keys(activeMetricsState.activeMetricMap ?? {})),
  ]);

  /**
   * @description create the filename for pdf export
   */

  const filename = useFilename(
    `${test.testType.abbr.toLowerCase()}`,
    test.athlete,
    formatDateWithTime(test.timestamp)
  );

  /// use the correct popup card depending on level
  const Card = level === 2 ? InnerPopupCard : PopupCard;

  return (
    <Card noClickOutside size='full' onClose={onClose}>
      <PopupCardContent>
        <CardHeader
          surface={8}
          style={{
            flex: 0,
          }}
        >
          <CardHeaderLeft>
            <SectionHeader
              entityName={test.athlete.name}
              entityImage={test.athlete.image}
              secondaryTextMajor={testMetaString(test.testType.name, test.tags)}
              secondaryTextMinor={formatDateWithTime(test.timestamp)}
            />
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={onClose} />
          </CardHeaderRight>
        </CardHeader>
        {!isPdf(pdfState) ? (
          <CardBody
            style={{
              backgroundColor: 'var(--color-surface-0)',
            }}
          >
            <Row
              shouldCollapse
              style={{
                overflowY: isPdf ? 'hidden' : 'auto',
              }}
            >
              <TestColumns.Left>
                <Scoring
                  metrics={metrics}
                  test={test}
                  style={{
                    flex: 8,
                  }}
                  level={level}
                />
              </TestColumns.Left>
              <TestColumns.Right>
                <ScrollableArea>
                  <React.Fragment>
                    <ForceTime
                      test={test}
                      repetitions={repetitions}
                      selectedRepetition={selectedRepetition}
                      selectedRepetitionChangeStream={
                        selectedRepetitionChangeStream
                      }
                      repetitionsLoading={repetitionsLoading}
                      graphData={graphData}
                      graphRaw={graphRaw}
                      timingEvents={timingEvents}
                      dataMin={dataMin}
                      isLoading={graphLoading}
                      shouldFetchData={false}
                      editRepetitions={editRepetitions}
                    />
                    {renderForceProfiles()}
                    {!isEmpty(copData) && (
                      <div
                        style={{
                          flex: 2,
                          display: 'flex',
                          flexDirection: 'row',
                          paddingBottom: 'var(--grid-gap)',
                        }}
                      >
                        <COPGraph cop={copData} pdfMode={pdfState} />
                      </div>
                    )}
                  </React.Fragment>
                </ScrollableArea>
              </TestColumns.Right>
            </Row>
          </CardBody>
        ) : (
          <PdfContainer>
            <Pdf
              onComplete={() => {}}
              filename={filename}
              title='Test Report'
              pdfMode={pdfState}
              loading={false}
              onPdfModeChange={handlePdfModeChange}
              reportState={{ isValid: true }}
              entity={test.athlete}
            >
              <Notable pdf level={level}>
                <ForceTime
                  pdfMode={pdfState}
                  test={test}
                  style={{
                    height: '400px',
                  }}
                  repetitions={repetitions}
                  selectedRepetition={selectedRepetition}
                  selectedRepetitionChangeStream={
                    selectedRepetitionChangeStream
                  }
                  shouldFetchData={false}
                  graphData={graphData}
                  graphRaw={graphRaw}
                  timingEvents={timingEvents}
                  dataMin={dataMin}
                  isLoading={graphLoading}
                  editRepetitions={editRepetitions}
                />
                {renderForceProfiles()}
                {!isEmpty(copData) && (
                  <COPGraph
                    cop={copData}
                    pdfMode={pdfState}
                    style={{ height: '300px', marginTop: '20px' }}
                  />
                )}
              </Notable>

              <Scoring metrics={metrics} test={test} isPdf level={level} />
            </Pdf>
          </PdfContainer>
        )}
        <CardFooter
          style={{
            justifyContent: 'center',
          }}
        >
          <PdfStateToggle onChange={handlePdfModeChange} value={pdfState} />
        </CardFooter>
      </PopupCardContent>
    </Card>
  );
};

const TestColumns = {
  Left: styled(FullHeightColumn)`
    flex: 40;
    padding-bottom: var(--grid-gap);
    padding-left: var(--grid-gap);
    padding-right: var(--grid-gap);
    @media (max-width: 990px) {
      order: 2;
      padding-left: 5px;
      padding-right: 0px;
    }
  `,
  Right: styled(FullHeightColumn)`
    flex: 60;
    padding-right: var(--grid-gap);
    padding-bottom: var(--grid-gap);
    @media (max-width: 990px) {
      order: 1;
      padding-left: 5px;
      padding-right: 0px;
      padding-bottom: 0px;
    }
    display: flex;
    flex-direction: column;
  `,
};
